import { css, useTheme } from '@emotion/react';
import { ChevronRight, Mic, Music, Pencil, Play, Plus, Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { fontSizes, spacing } from '@/theme/tokens';

interface EmptyStateProps {
  onCreateScript: () => void;
}

const STEPS = [
  { label: 'Write', description: 'Write or paste your speech script', icon: 'edit' },
  { label: 'Analyze', description: 'AI detects sections and tone', icon: 'search' },
  { label: 'Rehearse', description: 'Practice and capture your pace', icon: 'mic' },
  { label: 'Generate', description: 'AI creates custom background music', icon: 'music' },
  { label: 'Present', description: 'Deliver with synced music playback', icon: 'play' },
] as const;

function StepIcon({ icon }: { icon: string }) {
  const iconMap: Record<string, React.JSX.Element> = {
    edit: <Pencil size={20} aria-hidden="true" />,
    search: <Search size={20} aria-hidden="true" />,
    mic: <Mic size={20} aria-hidden="true" />,
    music: <Music size={20} aria-hidden="true" />,
    play: <Play size={20} aria-hidden="true" />,
  };
  return iconMap[icon] ?? null;
}

export function EmptyState({ onCreateScript }: EmptyStateProps) {
  const theme = useTheme();

  return (
    <div
      data-testid="empty-state"
      css={css`
        text-align: center;
        padding: ${spacing.xxl} ${spacing.lg};
      `}
    >
      <h2
        css={css`
          margin: 0 0 ${spacing.sm};
          font-size: ${fontSizes.xxl};
          color: ${theme.colors.text};
        `}
      >
        Welcome to Sonematic
      </h2>
      <p
        css={css`
          margin: 0 0 ${spacing.xl};
          color: ${theme.colors.textSecondary};
          font-size: ${fontSizes.md};
        `}
      >
        Create background music for your speeches in 5 simple steps
      </p>

      <div
        css={css`
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: ${spacing.md};
          margin-bottom: ${spacing.xl};
        `}
      >
        {STEPS.map((step, i) => (
          <div
            key={step.label}
            css={css`
              display: flex;
              align-items: center;
              gap: ${spacing.sm};
            `}
          >
            <div
              css={css`
                display: flex;
                flex-direction: column;
                align-items: center;
                gap: ${spacing.xs};
                width: 100px;
              `}
            >
              <div
                css={css`
                  width: 40px;
                  height: 40px;
                  border-radius: 50%;
                  background: ${theme.colors.primary}15;
                  color: ${theme.colors.primary};
                  display: flex;
                  align-items: center;
                  justify-content: center;
                `}
              >
                <StepIcon icon={step.icon} />
              </div>
              <span
                css={css`
                  font-size: ${fontSizes.sm};
                  font-weight: 600;
                  color: ${theme.colors.text};
                `}
              >
                {step.label}
              </span>
              <span
                css={css`
                  font-size: ${fontSizes.xs};
                  color: ${theme.colors.textSecondary};
                  line-height: 1.3;
                `}
              >
                {step.description}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <ChevronRight
                size={16}
                aria-hidden="true"
                css={css`
                  flex-shrink: 0;
                  margin-top: -${spacing.lg};
                  color: ${theme.colors.textMuted};
                `}
              />
            )}
          </div>
        ))}
      </div>

      <Button
        size="lg"
        onClick={onCreateScript}
        css={css`
          display: inline-flex;
          align-items: center;
          gap: ${spacing.sm};
        `}
      >
        <Plus size={16} aria-hidden="true" />
        Create Your First Speech
      </Button>
    </div>
  );
}
